import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { StoryboardMetadata } from '../../../types';
import { getStoryboardSheetUrl } from '../../../utils/apiClient';

export interface StoryboardPreviewProps {
  gameId: string;
  feed: 'auto' | 'pano';
  time: number;
  metadata: StoryboardMetadata;
  authToken?: string | null;
  width?: number | string;
  height?: number | string;
  showTimecode?: boolean;
  border?: string;
  borderRadius?: number | string;
  boxShadow?: string;
  sx?: any;
}

const formatSeconds = (sec: number): string => {
  if (!sec || isNaN(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s < 10 ? '0' : ''}${s}`;
};

const calcPercent = (idx: number, count: number): string => {
  if (count <= 1 || idx <= 0) return '0%';
  if (idx >= count - 1) return '100%';
  return `${Number(((idx / (count - 1)) * 100).toFixed(4))}%`;
};

export const StoryboardPreview: React.FC<StoryboardPreviewProps> = ({
  gameId,
  feed,
  time,
  metadata,
  authToken,
  width = 160,
  height = 90,
  showTimecode = true,
  border = '1.5px solid #38bdf8',
  borderRadius = 1.5,
  boxShadow = '0 4px 16px rgba(0, 0, 0, 0.75)',
  sx = {},
}) => {
  const preloadedSheetsRef = useRef<Set<number>>(new Set());

  const interval = metadata.interval || 2;
  const tilesPerSheet = metadata.tilesPerSheet || 100;
  const cols = metadata.cols || 10;
  const rows = metadata.rows || 10;

  const frameIndex = Math.max(0, Math.floor(time / interval));
  const sheetIndex = Math.floor(frameIndex / tilesPerSheet);
  const clampedSheet = Math.min(sheetIndex, Math.max(0, (metadata.sheetCount || 1) - 1));

  const indexInSheet = frameIndex % tilesPerSheet;
  const col = indexInSheet % cols;
  const row = Math.floor(indexInSheet / cols);

  const bgPosX = calcPercent(col, cols);
  const bgPosY = calcPercent(row, rows);

  const currentSheetUrl = useMemo(() => {
    return getStoryboardSheetUrl(gameId, feed, clampedSheet, authToken);
  }, [gameId, feed, clampedSheet, authToken]);

  // Preload current sheet and next sheet to eliminate flicker
  useEffect(() => {
    if (!preloadedSheetsRef.current.has(clampedSheet)) {
      const img = new Image();
      img.src = currentSheetUrl;
      preloadedSheetsRef.current.add(clampedSheet);
    }
    const nextSheet = clampedSheet + 1;
    if (nextSheet < metadata.sheetCount && !preloadedSheetsRef.current.has(nextSheet)) {
      const nextImg = new Image();
      nextImg.src = getStoryboardSheetUrl(gameId, feed, nextSheet, authToken);
      preloadedSheetsRef.current.add(nextSheet);
    }
  }, [clampedSheet, currentSheetUrl, gameId, feed, metadata.sheetCount, authToken]);

  return (
    <Box
      sx={{
        width,
        height,
        borderRadius,
        overflow: 'hidden',
        bgcolor: '#09090b',
        border: border || 'none',
        boxShadow: boxShadow || 'none',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        ...sx,
      }}
    >
      <Box
        data-testid="storyboard-sprite"
        style={{
          backgroundImage: `url(${currentSheetUrl})`,
          backgroundPosition: `${bgPosX} ${bgPosY}`,
          backgroundSize: `${cols * 100}% ${rows * 100}%`,
          backgroundRepeat: 'no-repeat',
        }}
        sx={{
          position: 'absolute',
          inset: 0,
        }}
      />
      {showTimecode && (
        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            bgcolor: 'rgba(0, 0, 0, 0.75)',
            px: 0.75,
            py: 0.25,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderTop: '1px solid rgba(255, 255, 255, 0.15)',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: '#fff',
              fontSize: '0.7rem',
              fontWeight: 700,
              fontFamily: 'monospace',
              letterSpacing: 0.5,
              textShadow: '0 1px 2px rgba(0,0,0,0.8)',
            }}
          >
            {formatSeconds(time)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
